import React, { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { Layout } from "@/components/Layout";
import { Loader2 } from "lucide-react";
import Index from "./pages/Index";
import Accounts from "./pages/Accounts";
import SegmentDirectory from "./pages/SegmentDirectory";
import AccountDetail from "./pages/AccountDetail";
import ListBuilder from "./pages/ListBuilder";
import News from "./pages/News";
import Newsletter from "./pages/Newsletter";

import Login from "./pages/Login";

import CompanyTracking from "./pages/CompanyTracking";
import TargetUniverse from "./pages/TargetUniverse";
import EventIntelligence from "./pages/EventIntelligence";
import EventDetail from "./pages/EventDetail";
import ResearchLibrary from "./pages/ResearchLibrary";
import ContentStudio from "./pages/ContentStudio";
import SocialListening from "./pages/SocialListening";
import TeamProfileInterview from "./pages/TeamProfileInterview";
import AcademicInstitutionDetail from "./pages/AcademicInstitutionDetail";
import NotFound from "./pages/NotFound";

const CampaignHub = React.lazy(() => import("./pages/CampaignHub"));
const CampaignTasks = React.lazy(() => import("./pages/CampaignTasks"));
const MarketIntel = React.lazy(() => import("./pages/MarketIntel"));
const Metrics = React.lazy(() => import("./pages/Metrics"));
const AcademicMarket = React.lazy(() => import("./pages/AcademicMarket"));
const UserGuide = React.lazy(() => import("./pages/UserGuide"));
const CampaignDetail = React.lazy(() => import("./pages/CampaignDetail"));

const PageLoader = () => (
  <div className="flex items-center justify-center h-[60vh]">
    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
  </div>
);

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            {/* Suspense boundary for lazy-loaded pages */}
              <Route path="/" element={<Index />} />
              <Route path="/accounts" element={<Accounts />} />
              <Route path="/service-providers" element={<SegmentDirectory segment="cro_cdmo" title="Service Providers" subtitle="CRO & CDMO manufacturing and research partners" requireIcpRelevant={false} exportStem="carr_service_providers" variant="service" />} />
              <Route path="/needs-review" element={<SegmentDirectory segment="review" title="Needs Review" subtitle="Contacted or misclassified accounts held for manual triage" requireIcpRelevant={false} exportStem="carr_needs_review" variant="review" />} />
              <Route path="/accounts/:id" element={<AccountDetail />} />
              <Route path="/academic-market" element={<Suspense fallback={<PageLoader />}><AcademicMarket /></Suspense>} />
              <Route path="/academic-market/:id" element={<AcademicInstitutionDetail />} />
              <Route path="/list-builder" element={<ListBuilder />} />
              <Route path="/campaigns" element={<Navigate to="/campaigns/hub" replace />} />
              <Route path="/campaigns/hub" element={<Suspense fallback={<PageLoader />}><CampaignHub /></Suspense>} />
              <Route path="/campaigns/tracker" element={<Navigate to="/campaigns/hub" replace />} />
              <Route path="/campaigns/tasks" element={<Suspense fallback={<PageLoader />}><CampaignTasks /></Suspense>} />
              <Route path="/campaigns/:id" element={<Suspense fallback={<PageLoader />}><CampaignDetail /></Suspense>} />
              <Route path="/reports/campaigns" element={<Navigate to="/campaigns/hub" replace />} />
              <Route path="/news" element={<News />} />
              <Route path="/newsletter" element={<Newsletter />} />
              <Route path="/company-tracking" element={<CompanyTracking />} />
              <Route path="/target-universe" element={<TargetUniverse />} />
              <Route path="/tasks" element={<Navigate to="/campaigns/tasks" replace />} />
              <Route path="/events" element={<EventIntelligence />} />
              <Route path="/events/:id" element={<EventDetail />} />
              <Route path="/research-library" element={<ResearchLibrary />} />
              <Route path="/research-library/:slug" element={<ResearchLibrary />} />
              <Route path="/market-intel" element={<Suspense fallback={<PageLoader />}><MarketIntel /></Suspense>} />
              <Route path="/metrics" element={<Suspense fallback={<PageLoader />}><Metrics /></Suspense>} />
              <Route path="/content-studio" element={<ContentStudio />} />
              <Route path="/social-listening" element={<SocialListening />} />
              <Route path="/team-profiles" element={<TeamProfileInterview />} />
              
              <Route path="/user-guide" element={<Suspense fallback={<PageLoader />}><UserGuide /></Suspense>} />
              
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
